import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Cart, Heart, List, Person, Search as SearchIcon, X } from 'react-bootstrap-icons';
import Swal from 'sweetalert2';

import Categorysection from './Categorysection';
import SearchBox from './SearchBox';
import { clearAuth, useAuthState } from '../../hooks/useAuthStorage';
import { useShopUI } from '../../context/ShopUIContext';
// The full lockup: earring above the type. Trimmed to the ink — the
// original art is a 500x500 canvas that is mostly empty white, so at
// header size it rendered about a third of its box and was unreadable.
import logo from '../../images/logo-lockup.png';
import './Header.scss';

const PRIMARY_LINKS = [
  { to: '/', label: 'Home', end: true },
  { to: '/allproducts', label: 'Shop All' },
  { to: '/all-categories', label: 'Categories' },
  { to: '/aboutus', label: 'About Us' },
];

const CONTACT_LINKS = [
  { href: 'mailto:bansalattire@gmail.com', label: 'Email us' },
  { href: 'tel:+919784889134', label: 'Call for help' },
  { href: 'https://wa.me/+919784889134', label: 'Chat on WhatsApp', external: true },
];

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoggedIn, userName } = useAuthState();
  // The auth and cart modals are owned by ShopUIProvider so a single
  // instance is shared by the header, the product grids and the cart.
  const { openLogin, openSignup, openCart } = useShopUI();

  const isCheckOutPage = location.pathname.startsWith('/check/checkout');

  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const menuToggleRef = useRef(null);
  const drawerRef = useRef(null);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    setContactOpen(false);
  }, []);

  // Close the drawer whenever the route changes — tapping a link used
  // to leave the overlay open behind the new page on iOS.
  useEffect(() => {
    closeMenu();
    setMobileSearchOpen(false);
  }, [location.pathname, location.search, closeMenu]);

  // Lock body scroll behind the drawer so the page underneath does not
  // scroll away while the menu is open.
  useEffect(() => {
    if (!menuOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [menuOpen]);

  // Escape closes the drawer and returns focus to the toggle.
  useEffect(() => {
    if (!menuOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMenu();
        menuToggleRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [menuOpen, closeMenu]);

  // Move focus into the drawer when it opens so keyboard and screen
  // reader users are not left behind on the page underneath.
  useEffect(() => {
    if (menuOpen) drawerRef.current?.focus();
  }, [menuOpen]);

  const handleLogout = () => {
    clearAuth();
    if (isCheckOutPage) navigate('/');
    closeMenu();
    Swal.fire({
      icon: 'success',
      title: 'Logged out',
      confirmButtonColor: '#a17e33',
    });
  };

  const handleDrawerLogin = () => {
    closeMenu();
    openLogin();
  };

  const handleDrawerSignup = () => {
    closeMenu();
    openSignup();
  };

  return (
    <>
      <header className="site-header">
        <div className="site-header__inner">
          {/* --- Mobile: menu toggle --- */}
          <button
            type="button"
            ref={menuToggleRef}
            className="site-header__icon-btn site-header__menu-toggle"
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            aria-controls="mobile-nav-drawer"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          >
            {menuOpen ? <X size={24} aria-hidden="true" /> : <List size={24} aria-hidden="true" />}
          </button>

          {/* --- Logo --- */}
          <Link to="/" className="site-header__logo" aria-label="Bansal Attire — home">
            <img src={logo} alt="Bansal Attire" width="467" height="299" />
          </Link>

          {/* --- Desktop search --- */}
          <div className="site-header__search">
            <SearchBox />
          </div>

          {/* --- Desktop navigation --- */}
          <nav className="site-header__nav" aria-label="Main">
            {PRIMARY_LINKS.map(({ to, label, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  `site-header__nav-link${isActive ? ' is-active' : ''}`
                }
              >
                {label}
              </NavLink>
            ))}
          </nav>

          {/* --- Actions --- */}
          <div className="site-header__actions">
            <button
              type="button"
              className="site-header__icon-btn site-header__search-toggle"
              onClick={() => setMobileSearchOpen((open) => !open)}
              aria-expanded={mobileSearchOpen}
              aria-label={mobileSearchOpen ? 'Close search' : 'Open search'}
            >
              {mobileSearchOpen ? <X size={20} aria-hidden="true" /> : <SearchIcon size={19} aria-hidden="true" />}
            </button>

            <Link to="/wishlist" className="site-header__icon-btn" aria-label="Wishlist">
              <Heart size={19} aria-hidden="true" />
            </Link>

            <Dropdown align="end" className="site-header__account">
              <Dropdown.Toggle
                as="button"
                type="button"
                className="site-header__icon-btn site-header__account-toggle"
                id="account-menu"
                aria-label="Account menu"
              >
                <Person size={20} aria-hidden="true" />
                <span className="site-header__account-name">
                  {isLoggedIn ? userName || 'Account' : 'Account'}
                </span>
              </Dropdown.Toggle>

              <Dropdown.Menu>
                {isLoggedIn ? (
                  <>
                    <div className="account-menu__title">My Account</div>
                    <Dropdown.Item as={Link} to="/check/profile">
                      Profile
                    </Dropdown.Item>
                    <Dropdown.Item as={Link} to="/check/profile/your-orders">
                      Your orders
                    </Dropdown.Item>
                    <Dropdown.Item as={Link} to="/wishlist">
                      Wishlist
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item as="button" type="button" onClick={handleLogout}>
                      Log out
                    </Dropdown.Item>
                  </>
                ) : (
                  <>
                    <div className="account-menu__title">My Account</div>
                    <div className="account-menu__subtitle">Sign in to track orders and save favourites.</div>
                    <div className="account-menu__actions">
                      <button type="button" className="btn btn--primary btn--sm" onClick={() => openLogin()}>
                        Login
                      </button>
                      <button type="button" className="btn btn--outline btn--sm" onClick={() => openSignup()}>
                        Sign up
                      </button>
                    </div>
                  </>
                )}
              </Dropdown.Menu>
            </Dropdown>

            <button
              type="button"
              className="site-header__icon-btn site-header__cart"
              onClick={openCart}
              aria-label="Open cart"
            >
              <Cart size={20} aria-hidden="true" />
              <span className="site-header__cart-label">Cart</span>
            </button>
          </div>
        </div>

        {/* --- Collapsible search row (tablet / mobile) --- */}
        {mobileSearchOpen && (
          <div className="site-header__search-row">
            <SearchBox autoFocus onNavigate={() => setMobileSearchOpen(false)} />
          </div>
        )}
      </header>

      {/* --- Mobile drawer --- */}
      <div
        className={`nav-drawer${menuOpen ? ' is-open' : ''}`}
        // `inert` would be ideal but is not supported everywhere yet;
        // hiding from the a11y tree keeps the closed drawer untabbable.
        aria-hidden={!menuOpen}
      >
        <button
          type="button"
          className="nav-drawer__scrim"
          onClick={closeMenu}
          tabIndex={-1}
          aria-hidden="true"
        />

        <div
          className="nav-drawer__panel"
          id="mobile-nav-drawer"
          role="dialog"
          aria-modal="true"
          aria-label="Site menu"
          ref={drawerRef}
          tabIndex={-1}
        >
          <div className="nav-drawer__header">
            <img src={logo} alt="Bansal Attire" width="467" height="299" />
            <button
              type="button"
              className="site-header__icon-btn"
              onClick={closeMenu}
              aria-label="Close menu"
            >
              <X size={24} aria-hidden="true" />
            </button>
          </div>

          <nav className="nav-drawer__nav" aria-label="Mobile">
            {PRIMARY_LINKS.map(({ to, label, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) => `nav-drawer__link${isActive ? ' is-active' : ''}`}
                onClick={closeMenu}
              >
                {label}
              </NavLink>
            ))}

            <NavLink to="/wishlist" className="nav-drawer__link" onClick={closeMenu}>
              Wishlist
            </NavLink>

            <button
              type="button"
              className="nav-drawer__link nav-drawer__link--button"
              onClick={() => setContactOpen((open) => !open)}
              aria-expanded={contactOpen}
              aria-controls="drawer-contact"
            >
              Contact Us
              <span className={`nav-drawer__chevron${contactOpen ? ' is-open' : ''}`} aria-hidden="true">
                ›
              </span>
            </button>

            <div id="drawer-contact" className={`nav-drawer__sublist${contactOpen ? ' is-open' : ''}`}>
              {CONTACT_LINKS.map(({ href, label, external }) => (
                <a
                  key={href}
                  href={href}
                  className="nav-drawer__sublink"
                  {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                >
                  {label}
                </a>
              ))}
            </div>

            <NavLink to="/tac" className="nav-drawer__link" onClick={closeMenu}>
              Terms &amp; Conditions
            </NavLink>
          </nav>

          <div className="nav-drawer__footer">
            {isLoggedIn ? (
              <>
                <Link className="btn btn--primary btn--block" to="/check/profile" onClick={closeMenu}>
                  {userName ? `Profile · ${userName}` : 'Profile'}
                </Link>
                <button type="button" className="btn btn--outline btn--block" onClick={handleLogout}>
                  Log out
                </button>
              </>
            ) : (
              <>
                <button type="button" className="btn btn--primary btn--block" onClick={handleDrawerLogin}>
                  Login
                </button>
                <button type="button" className="btn btn--outline btn--block" onClick={handleDrawerSignup}>
                  Sign up
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <Categorysection />
    </>
  );
};

export default Header;
